import { GATEWAY_ORIGIN } from '@/constants/environment';

/**
 * NBC API Configuration
 *
 * Non Bookable Content API provides supplementary product information
 * that complements Player API data.
 *
 * @module services/api/nbc/config
 */

/**
 * Shared NBC request behaviour (timeout + retry) used by all NBC clients.
 */
const NBC_REQUEST_DEFAULTS = {
  TIMEOUT: 10000,
  RETRY: {
    maxAttempts: 2,
    initialDelay: 500,
  },
} as const;

export const NBC_CONFIG = {
  /**
   * Base API endpoint
   *
   * In development: Uses Vite proxy at /api/nbc to avoid CORS issues
   * In production: Uses direct HTTPS endpoint (with /de/ locale prefix)
   */
  ENDPOINT: import.meta.env.DEV ? '/api/nbc' : `${GATEWAY_ORIGIN}/de/nbc`,
  ...NBC_REQUEST_DEFAULTS,
} as const;

/**
 * API endpoint paths
 */
export const NBC_ENDPOINTS = {
  /**
   * Single product details
   * @param productId - Product ID from Player API
   * @example /product/3123123
   */
  PRODUCT: (productId: string) => `/product/${productId}`,

  /**
   * Multiple products list
   * @param ids - Comma-separated product IDs
   * @example /product-list?ids=123,456,789
   */
  PRODUCT_LIST: '/product-list',
} as const;

/**
 * NBC Holidays API configuration
 *
 * Holiday endpoints live on the locale-agnostic NBC base URL (without /de/),
 * unlike product endpoints which require the /de/ locale prefix.
 * Dev: handled by vite proxy at /api/nbc (see vite.config.ts).
 */
export const NBC_HOLIDAYS_CONFIG = {
  ENDPOINT: import.meta.env.DEV ? '/api/nbc' : `${GATEWAY_ORIGIN}/nbc`,
  ...NBC_REQUEST_DEFAULTS,
} as const;

export const NBC_HOLIDAYS_ENDPOINTS = {
  /**
   * List of states for a country.
   * @example /holidays/deutschland
   */
  STATES: (country: string) => `/holidays/${country}`,

  /**
   * School holidays for a country + state.
   * Without `year` the API returns the current year only.
   * @example /holidays/deutschland/bayern/school-holidays
   * @example /holidays/deutschland/bayern/school-holidays/2027
   */
  SCHOOL_HOLIDAYS: (country: string, state: string, year?: number) =>
    `/holidays/${country}/${state}/school-holidays${year ? `/${year}` : ''}`,
} as const;
